import React, { useEffect, useId, useRef } from 'react';
import Icon from '../Icon';
import Button from './Button';
import '../../styles/components/LineItemsList.css';

/** What a column's renderer is told about the line it draws. */
export interface LineCellContext {
  index: number;
  /** The whole list is read-only (a viewer, a record that cannot be edited). */
  disabled: boolean;
  /** Accessible name for the control: "<column header>, <line name>". Use it as aria-label. */
  ariaLabel: string;
  /** Id of the line's error line, when it has one; use it as aria-describedby. */
  errorId: string | undefined;
}

export interface LineColumn<T> {
  key: string;
  /** Shown above the control; also part of its accessible name. */
  header: string;
  render: (line: T, ctx: LineCellContext) => React.ReactNode;
  /** Takes a full row on its own (a long list of tags, a text area). */
  wide?: boolean;
}

export interface LineItemsListProps<T> {
  /** Names the list for screen readers ("Project roles"). */
  label: string;
  lines: readonly T[];
  /** A key that stays the same while the line is edited. */
  getKey: (line: T) => string;
  /** What the line is called in labels ("Eko & Oceania Wedding"); falls back to "line N". */
  lineName?: (line: T, index: number) => string;
  columns: readonly LineColumn<T>[];
  /** Shows an Add button. Without it the list can only be edited, not extended. */
  onAdd?: () => void;
  addLabel?: string;
  /** Why Add is off right now ("Every project is already listed"); shown beside it. */
  addDisabledReason?: string | null;
  /** Shows a Remove button on each line. */
  onRemove?: (line: T, index: number) => void;
  /** One plain sentence per failed line, by line key. Shown inside the line, announced to screen readers. */
  errors?: Readonly<Record<string, string>>;
  emptyTitle: string;
  emptyHint?: string;
  /** Read-only: controls get ctx.disabled, Add and Remove are not drawn. */
  disabled?: boolean;
  /** Keeps a line from being removed (e.g. while it is being saved). */
  isLineLocked?: (line: T) => boolean;
}

const FOCUSABLE = 'input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * A generic, typed list of editable lines: each line is a card of labelled cells from `columns`, with a Remove
 * button, an Add button under the list, an empty state and one error line per failed line. It knows nothing about
 * what a line holds (the Edit user page uses it for project roles; quote or invoice lines can use it too).
 * For money lines with prices and totals see LineItemsEditor, which is built for that.
 *
 * Accessibility: a list of lines, each a labelled group; every control is named "<header>, <line>"; an error is a
 * role="alert" line tied to the controls. Focus moves to the first control of a new line and, after a Remove, to the
 * Add button. Below 640px the cells stack one per row.
 */
function LineItemsList<T>({
  label, lines, getKey, lineName, columns, onAdd, addLabel = 'Add line', addDisabledReason, onRemove,
  errors, emptyTitle, emptyHint, disabled = false, isLineLocked,
}: LineItemsListProps<T>): React.ReactElement {
  const uid = useId();
  const listRef = useRef<HTMLUListElement>(null);
  const addRef = useRef<HTMLButtonElement>(null);
  const previousCount = useRef(lines.length);

  useEffect(() => {
    const before = previousCount.current;
    previousCount.current = lines.length;
    if (lines.length > before) {
      const last = listRef.current?.lastElementChild;
      last?.querySelector<HTMLElement>(FOCUSABLE)?.focus();
    } else if (lines.length < before) {
      addRef.current?.focus();
    }
  }, [lines.length]);

  const nameOf = (line: T, index: number) => lineName?.(line, index).trim() || `line ${index + 1}`;
  const canAdd = !!onAdd && !disabled;
  const canRemove = !!onRemove && !disabled;

  return (
    <div className="lil">
      {lines.length === 0 ? (
        <div className="lil-empty" role="status">
          <p className="lil-empty__title">{emptyTitle}</p>
          {emptyHint && <p className="lil-empty__hint">{emptyHint}</p>}
        </div>
      ) : (
        <ul className="lil-list" ref={listRef} aria-label={label}>
          {lines.map((line, index) => {
            const key = getKey(line);
            const name = nameOf(line, index);
            const error = errors?.[key];
            const errorId = error ? `${uid}-${index}-error` : undefined;
            const locked = isLineLocked?.(line) ?? false;
            return (
              <li key={key} className={`lil-line${error ? ' lil-line--error' : ''}`} role="group" aria-label={name}>
                <div className="lil-line__cells">
                  {columns.map(col => (
                    <div key={col.key} className={`lil-cell${col.wide ? ' lil-cell--wide' : ''}`}>
                      <span className="lil-cell__label">{col.header}</span>
                      {col.render(line, { index, disabled: disabled || locked, ariaLabel: `${col.header}, ${name}`, errorId })}
                    </div>
                  ))}
                </div>
                {canRemove && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="lil-line__remove"
                    aria-label={`Remove ${name}`}
                    title="Remove this line"
                    disabled={locked}
                    icon={<Icon name="trash" size={14} />}
                    onClick={() => onRemove?.(line, index)}
                  >
                    <span className="lil-line__remove-text">Remove</span>
                  </Button>
                )}
                {error && <p id={errorId} className="lil-line__error form-error-text" role="alert">{error}</p>}
              </li>
            );
          })}
        </ul>
      )}
      {canAdd && (
        <div className="lil-add">
          <Button
            ref={addRef}
            type="button"
            variant="secondary"
            size="sm"
            icon={<Icon name="plus" size={14} />}
            disabled={!!addDisabledReason}
            onClick={onAdd}
          >
            {addLabel}
          </Button>
          {addDisabledReason && <span className="lil-add__reason">{addDisabledReason}</span>}
        </div>
      )}
    </div>
  );
}

export default LineItemsList;
